'use client'

import React from 'react'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogAppCTA, BlogWhatTheAppChecks, BlogSources, BlogTLDR, BlogKeyTakeaways, BlogFAQ } from '@/app/components/blog/BlogAeo'
import Link from 'next/link'

export default function Post15Content() {
  return (
    <BlogPostLayout
      title="Microplastics in Clothing: The Microfiber Problem"
      author="MicroPlastics Team"
      date="December 3, 2025"
      readTime="8 min read"
      heroImage="/blog/app-search.png"
      heroImageAlt="MicroPlastics app analyzing clothing materials"
    >
      <BlogTLDR>
        Synthetic clothing (polyester, nylon, acrylic, fleece) sheds{' '}
        <strong>hundreds of thousands of microfibres per load of laundry</strong>{' '}, one of the largest sources of microplastics entering oceans. Worst
        offenders: fleece, microfibre towels, athleisure. Natural fibres
        (cotton, wool, linen, hemp, Tencel) shed virtually no microplastics.
        Mitigation: wash less, wash cold, full loads, Guppyfriend bag or
        PlanetCare filter, line dry.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>A single load of synthetic laundry can release 700,000+ microfibres into wastewater.</>,
          <>Worst fabrics: fleece, polyester athleisure, nylon, &ldquo;microfibre&rdquo; cleaning cloths.</>,
          <>Natural fibres (cotton, wool, linen, hemp, Tencel/lyocell) shed virtually no microplastics.</>,
          <>Mitigations: cold wash, full loads, Guppyfriend bag, PlanetCare washing-machine filter, line drying.</>,
          <>Shedding rises with wash temperature, so washing cold cuts fibre release directly, it is not just an energy tip.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Introduction: The Clothes on Your Back Are Shedding Plastic
      </h2>
      <p>
        Every time you wash a load of laundry, hundreds of thousands of tiny plastic fibers break free from your clothes and flow down the drain. These fibers, known as <strong>microfibers</strong>, are one of the largest and most overlooked sources of microplastic pollution on the planet. They are too small for most wastewater treatment plants to capture, so they pass through filtration systems and enter rivers, lakes, and oceans, eventually making their way into the food chain and the air we breathe.
      </p>
      <p>
        The clothing industry has undergone a massive transformation over the past few decades. Synthetic fabrics like polyester, nylon, and acrylic now dominate the market, accounting for roughly 60 to 70 percent of all textiles produced globally. These materials are affordable, durable, and versatile, but they come with a hidden cost: they shed microplastic fibers continuously, during manufacturing, while being worn, and especially during washing. Understanding the microfiber problem is essential for anyone trying to reduce their microplastic exposure. For a broader look at reducing microplastic exposure in everyday life, see our guide on{' '}
        <Link href="/how-to-avoid-microplastics" className="text-[#1E6B47] hover:underline font-medium">
          how to avoid microplastics
        </Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        2026: Wash Cold, and Mind the Car
      </h2>
      <p>
        Two findings tighten the guidance on synthetic clothing, and both are free to act on.
      </p>
      <p>
        The first concerns temperature. Research on synthetic fabrics including polyester and
        acetate found that <strong>microfibre shedding increases as wash temperature
        rises</strong>. Washing cold is not merely an energy-saving tip -- it directly reduces
        the number of fibres your clothes release, into the wastewater stream and onto the
        next load.
      </p>
      <p>
        The second reframes where those fibres end up affecting you. Indoor-air measurements
        published in <em>PLOS One</em> found car cabins running at{' '}
        <strong>2,238 lung-penetrating particles per cubic metre</strong> against{' '}
        <strong>528 in homes</strong> -- and in the car the dominant polymer was{' '}
        <strong>polyamide</strong>, which is nylon: carpet, seat fabric, headliner. Textiles
        are not only something you wear and wash. They are the main furnishing of the
        highest-concentration environment most people sit in every day.
      </p>
      <p>
        That gives a compact set of actions with real evidence behind them:{' '}
        <strong>wash synthetics cold and less often</strong>, use a filter bag or in-line
        laundry filter, choose wool, cotton and linen where the garment allows, and{' '}
        <strong>ventilate the car</strong> -- especially when it has been sitting in the sun,
        which drives shedding from a cabin full of nylon.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What Are Microfibers?
      </h2>
      <p>
        Microfibers are extremely thin strands of material, typically less than 5 millimeters in length and often much smaller, with diameters measured in micrometers. When these fibers come from synthetic textiles, they are classified as microplastics because they are made from petroleum-based polymers such as polyester (polyethylene terephthalate, or PET), nylon (polyamide), and acrylic (polyacrylonitrile). A single polyester fiber can be 10 to 30 micrometers in diameter, roughly one-fifth the width of a human hair.
      </p>
      <p>
        Unlike larger pieces of plastic debris that you can see and pick up, microfibers are nearly invisible. They are light enough to become airborne, meaning you can inhale them in your home, office, or car. They are also small enough to pass through the filters in washing machines and wastewater treatment plants, entering aquatic ecosystems where they are ingested by fish, shellfish, and other marine organisms. From there, microfibers travel up the food chain and onto our plates.
      </p>
      <p>
        Natural fibers like cotton and wool also shed during washing, but these fibers are biodegradable and break down relatively quickly in the environment. Synthetic microfibers, on the other hand, can persist for hundreds of years, accumulating in sediments, water supplies, and living organisms indefinitely.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Scale of the Problem: How Many Fibers Per Wash?
      </h2>
      <p>
        The numbers are staggering. A landmark 2016 study by researchers at Plymouth University found that a single wash load of synthetic clothing can release up to <strong>700,000 microfibers</strong> into wastewater. That is not a typo. Seven hundred thousand plastic fibers from a single cycle of your washing machine. Acrylic fabrics were the worst offenders in the study, releasing nearly 730,000 fibers per wash, compared to roughly 496,000 for polyester-cotton blends and 137,000 for pure polyester.
      </p>
      <p>
        Other studies have confirmed and expanded on these findings. Research published in Environmental Science and Technology estimated that between 0.6 and 1.7 million microfibers are released per wash load depending on the type of fabric, the washing temperature, and the machine used. A 2020 study in the journal Nature Reviews Earth and Environment estimated that synthetic textiles release approximately 35 percent of all primary microplastics entering the ocean, making laundry one of the single largest sources of marine microplastic pollution worldwide.
      </p>
      <p>
        To put this in perspective, the International Union for Conservation of Nature (IUCN) estimates that synthetic textiles contribute roughly 0.5 million tons of microfibers to the ocean every year. That is equivalent to dumping more than 50 billion plastic bottles into the sea annually. And because microfibers are so small and dispersed, cleaning them up from the ocean is virtually impossible with current technology.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Which Fabrics Shed the Most?
      </h2>
      <p>
        Synthetic fabrics are not equally bad for microfiber shedding. Research has consistently shown that certain materials are significantly worse offenders than others.
      </p>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Acrylic:</strong> Acrylic fabrics, commonly found in sweaters, scarves, and knitted garments, are the worst shedders. The Plymouth University study found acrylic released up to five times more microfibers per wash than polyester. The loose, fuzzy texture of acrylic knits makes fibers especially prone to breaking free.
        </li>
        <li>
          <strong>Polyester:</strong> Polyester is the most widely produced synthetic fabric in the world, used in everything from athletic wear and t-shirts to bedding and curtains. While individual polyester garments may shed fewer fibers per wash than acrylic, the sheer volume of polyester in circulation means it contributes the largest total amount of microfiber pollution globally.
        </li>
        <li>
          <strong>Nylon:</strong> Nylon is used in stockings, swimwear, activewear, and outdoor gear. It sheds fewer fibers than acrylic but more than tightly woven polyester. Nylon microfibers are particularly persistent in marine environments due to their chemical stability.
        </li>
        <li>
          <strong>Polyester-cotton blends:</strong> Blended fabrics are extremely common in everyday clothing. Research from the University of California, Santa Barbara found that polyester-cotton blends shed more microfibers than pure polyester, likely because the cotton fibers weaken the fabric structure and create more opportunities for synthetic fibers to break free.
        </li>
        <li>
          <strong>Fleece:</strong> Fleece jackets and blankets, typically made from polyester, are among the most prolific microfiber shedders. A single fleece jacket can release up to 250,000 microfibers in a single wash. The soft, brushed texture of fleece is designed to trap air for warmth, but it also means fibers are loosely bound and easily dislodged.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Fast Fashion and the Microfiber Crisis
      </h2>
      <p>
        The rise of fast fashion has dramatically accelerated the microfiber problem. Fast fashion brands produce enormous quantities of cheap clothing using low-cost synthetic fabrics, primarily polyester. Global polyester production has more than doubled since 2000, reaching over 60 million metric tons per year. The average consumer now buys 60 percent more clothing than they did two decades ago, but keeps each garment for only half as long.
      </p>
      <p>
        Cheaper synthetic garments tend to shed more microfibers than higher-quality alternatives because they are made with lower-grade yarns, looser weaves, and less durable construction. The faster a garment wears out, the more fibers it releases over its shortened lifespan. When these garments are discarded, they often end up in landfills where they continue to shed microfibers into the soil and groundwater for decades, or they are exported to developing countries where they may be burned or dumped in open environments.
      </p>
      <p>
        The sheer scale of fast fashion consumption compounds the problem. If billions of garments are being washed weekly around the world, and each wash releases hundreds of thousands of fibers, the cumulative microfiber output is almost incomprehensible. The fashion industry is now recognized as one of the most significant contributors to microplastic pollution, alongside tire wear and plastic packaging.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Health Risks of Microfiber Exposure
      </h2>
      <p>
        Microfibers do not just pollute the environment. They end up in our bodies, and emerging research suggests this could have serious health consequences.
      </p>
      <p>
        <strong>Inhalation:</strong> Airborne microfibers are present in indoor environments at concentrations that may surprise you. Studies have found that indoor air can contain 1 to 60 microfibers per cubic meter, with higher concentrations in homes with more synthetic textiles, carpeting, and upholstered furniture. Textile workers exposed to synthetic fiber dust have shown elevated rates of respiratory problems including coughing, breathlessness, and reduced lung function. A 2021 study published in the Journal of Hazardous Materials found synthetic microfibers in human lung tissue for the first time, confirming that these fibers can penetrate deep into the respiratory system and accumulate there.
      </p>
      <p>
        <strong>Ingestion:</strong> We also ingest microfibers through food and drinking water. Microfibers have been detected in tap water, bottled water, sea salt, honey, beer, and a wide range of seafood. A 2019 analysis estimated that the average person ingests approximately 5 grams of microplastics per week, with microfibers making up a significant portion of that total. Once ingested, microfibers can cause inflammation in the gut, disrupt the gut microbiome, and potentially cross into the bloodstream. To learn more about microplastic sources inside the home, see our article on{' '}
        <Link href="/blog/microplastics-in-your-home" className="text-[#1E6B47] hover:underline font-medium">
          microplastics in your home
        </Link>.
      </p>
      <p>
        <strong>Chemical exposure:</strong> Synthetic microfibers are not just inert plastic. They carry a cocktail of chemical additives used during textile manufacturing, including dyes, flame retardants, plasticizers, and antimicrobial treatments. Many of these chemicals are known or suspected endocrine disruptors. Additionally, microfibers in the environment can adsorb pollutants from surrounding water, including heavy metals, pesticides, and persistent organic pollutants, concentrating these toxins and delivering them into organisms that ingest the fibers.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Solutions: How to Reduce Microfiber Shedding at Home
      </h2>
      <p>
        While we cannot eliminate microfiber shedding entirely with current technology, there are several practical steps you can take to significantly reduce the number of fibers released from your laundry.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Washing Machine Filters and Catching Devices</h3>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Guppyfriend Washing Bag:</strong> This is a specially designed mesh laundry bag that captures microfibers released during the wash cycle. You place your synthetic garments inside the bag before putting it in the machine. Studies have shown the Guppyfriend can reduce microfiber release by up to 86 percent. After washing, you simply remove the collected fibers from the bag and dispose of them in the trash rather than letting them wash down the drain.
        </li>
        <li>
          <strong>Cora Ball:</strong> Inspired by the filtration mechanisms of coral, the Cora Ball is a laundry ball that you toss into the washing machine with your clothes. It captures microfibers as they swirl through the water. While not as effective as the Guppyfriend for individual garments, it is convenient because it works across the entire load without requiring you to bag each item.
        </li>
        <li>
          <strong>External washing machine filters:</strong> Some companies now manufacture aftermarket filters that attach to your washing machine&rsquo;s drain hose. These filters capture microfibers before they enter the wastewater system. France became the first country to mandate that all new washing machines include microfiber filters by 2025, and similar legislation is being considered in the UK, Australia, and California.
        </li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Laundry Habits That Make a Difference</h3>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Use a front-loading washing machine:</strong> Research has consistently found that front-loading machines release significantly fewer microfibers than top-loading machines. A study published in PLOS ONE found that top-loaders released up to seven times more microfibers per wash. The gentler tumbling action of front-loaders puts less mechanical stress on fabrics compared to the agitator in top-loaders.
        </li>
        <li>
          <strong>Wash in cold water:</strong> Higher water temperatures cause fabrics to expand and release more fibers. Washing at 30 degrees Celsius or lower can reduce microfiber shedding by up to 30 percent compared to washing at 40 degrees or higher.
        </li>
        <li>
          <strong>Use shorter wash cycles:</strong> Longer cycles mean more mechanical agitation and more fiber release. Using a quick or delicate cycle reduces the physical stress on garments and cuts microfiber shedding.
        </li>
        <li>
          <strong>Fill the machine fully:</strong> A full load creates less friction between garments than a half-empty machine, where clothes have more room to tumble and rub against each other and the drum.
        </li>
        <li>
          <strong>Use liquid detergent instead of powder:</strong> Powder detergents contain granules that act as abrasives against fabric surfaces, increasing fiber release. Liquid detergents are gentler on textiles.
        </li>
        <li>
          <strong>Wash less frequently:</strong> This is perhaps the simplest solution. Many garments, especially jeans, sweaters, and outerwear, do not need to be washed after every wear. Spot cleaning and airing out clothes between wears can dramatically reduce the total number of wash cycles and the corresponding fiber release.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Natural Fiber Alternatives
      </h2>
      <p>
        One of the most effective long-term strategies for reducing microfiber pollution is to shift toward clothing made from natural and sustainable fibers. While no textile is completely free of environmental impact, natural fibers biodegrade far more quickly than synthetics and do not contribute to permanent microplastic pollution.
      </p>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Organic cotton:</strong> Cotton is breathable, comfortable, and biodegradable. Organic cotton is grown without synthetic pesticides or fertilizers, making it a better choice for both personal health and the environment. However, conventional cotton farming is water-intensive, so organic and recycled cotton are preferred.
        </li>
        <li>
          <strong>Wool:</strong> Merino wool and other natural wools are excellent alternatives for sweaters, base layers, and activewear. Wool is naturally moisture-wicking, temperature-regulating, odor-resistant, and biodegradable. It does shed fibers, but these are natural protein fibers that break down in the environment.
        </li>
        <li>
          <strong>Linen:</strong> Made from flax plants, linen is one of the oldest textiles in human history. It is strong, breathable, naturally antibacterial, and fully biodegradable. Linen production requires less water and fewer pesticides than cotton.
        </li>
        <li>
          <strong>Hemp:</strong> Hemp fabric is durable, naturally antimicrobial, and requires very little water or pesticides to grow. It softens with each wash and is fully biodegradable. Hemp is increasingly being used in casual wear, denim, and accessories.
        </li>
        <li>
          <strong>Tencel (Lyocell):</strong> Tencel is a semi-synthetic fiber made from sustainably harvested wood pulp, typically eucalyptus. It is produced in a closed-loop process that recycles 99 percent of the solvents used. Tencel is soft, breathable, moisture-wicking, and biodegradable, making it an excellent alternative to polyester for activewear and everyday clothing.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What Brands and the Industry Are Doing
      </h2>
      <p>
        Awareness of the microfiber problem has grown significantly in recent years, and some clothing brands and industry organizations are beginning to take action.
      </p>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Patagonia</strong> was one of the first major outdoor brands to acknowledge the microfiber problem and fund research into it. They have invested in studying microfiber shedding from their fleece products and recommend that customers use the Guppyfriend washing bag.
        </li>
        <li>
          <strong>Adidas and Parley for the Oceans</strong> have partnered to create shoes and sportswear from recycled ocean plastic. While recycled polyester still sheds microfibers, it diverts existing plastic from the environment rather than producing new virgin plastic.
        </li>
        <li>
          <strong>The Microfibre Consortium</strong> is an industry-led initiative working with brands like H&M, Nike, and Lululemon to develop standardized testing methods for microfiber shedding and establish best practices for textile design that minimizes fiber release.
        </li>
        <li>
          <strong>Material innovation:</strong> Some companies are developing new synthetic fabrics engineered to shed fewer fibers. These include tighter weave structures, fiber coatings that reduce breakage, and yarn treatments that bind fibers more securely. While still in early stages, these innovations could significantly reduce shedding from synthetic garments in the future.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Legislation and Policy Progress
      </h2>
      <p>
        Governments around the world are beginning to recognize the microfiber problem and introduce legislation to address it. Progress is uneven, but the trend is moving in the right direction.
      </p>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>France</strong> passed a law in 2020 requiring all new washing machines sold in the country to include built-in microfiber filters starting in 2025. This was the first national mandate of its kind and has set a precedent for other countries.
        </li>
        <li>
          <strong>California</strong> passed Assembly Bill 1628 in 2023, which will require microfiber filtration on all new washing machines sold in the state starting in 2029. Given California&apos;s market influence, this is expected to push manufacturers to include filters as standard across the United States.
        </li>
        <li>
          <strong>The United Kingdom</strong> and <strong>Australia</strong> are both considering similar washing machine filter mandates, with active legislative proposals under review.
        </li>
        <li>
          <strong>The European Union</strong> has included microfiber pollution in its broader strategy on microplastics and is exploring requirements for textile labeling, washing instructions, and filtration technology.
        </li>
        <li>
          <strong>The United Nations</strong> Global Plastics Treaty, currently under negotiation, may include provisions addressing microfiber pollution from textiles as part of a comprehensive approach to reducing plastic pollution worldwide.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How the MicroPlastics App Can Help
      </h2>
      <p>
        Navigating the world of textile microplastics can be overwhelming. Which fabrics shed the most? Which brands are doing better? Is that &quot;eco-friendly&quot; label actually meaningful? The <strong>MicroPlastics app</strong> helps you cut through the confusion by letting you scan and search for products to understand their microplastic risk. Whether you are shopping for new clothes, choosing laundry products, or evaluating household textiles, the app gives you clear, actionable information so you can make smarter choices without spending hours on research.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Bottom Line
      </h2>
      <p>
        The clothes we wear are one of the biggest sources of microplastic pollution in the world. Every wash cycle sends hundreds of thousands of synthetic fibers into our waterways, our food supply, and our lungs. Fast fashion has amplified this crisis by flooding the market with cheap synthetic garments designed to be worn a few times and discarded.
      </p>
      <p>
        But there are real, practical solutions available today. Using microfiber-catching devices like the Guppyfriend washing bag or Cora Ball, switching to front-loading machines, washing in cold water with shorter cycles, and gradually transitioning your wardrobe toward natural fibers like cotton, wool, linen, hemp, and Tencel can all make a meaningful difference. Supporting brands that are investing in microfiber reduction and advocating for washing machine filter legislation can help drive systemic change.
      </p>
      <p>
        The microfiber problem is not going away on its own. But with informed choices and simple habit changes, you can significantly reduce your contribution to it while protecting your health and the health of the planet. Start by looking at the labels in your closet, and the next time you do laundry, think about what is going down the drain.
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>Fibre composition from the label, cotton, linen, wool, polyester, nylon, blends.</>,
          <>Brand and product type, flags for known PFAS-treated water-resistant fabrics.</>,
          <>Care label cues, wash temperature, tumble-dry guidance.</>,
          <>Cited research on textile microfibre shedding.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan clothing and bedding tags before buying"
        sub="Polyester sheds microfibres with every wear. The MicroPlastics app reads composition labels and flags synthetic-heavy items vs natural-fibre alternatives."
        buttonLabel="Scan textiles in the app"
      />


      <BlogFAQ
        items={[
          {
            question: 'Which fabrics shed the most microplastics?',
            answer:
              'Fleece (made from polyester or acrylic), nylon stockings, microfibre cleaning cloths, polyester athleisure (yoga pants, sports bras), and acrylic sweaters. Mechanical friction and warm/hot water both accelerate shedding.',
          },
          {
            question: 'Do cotton clothes shed microplastics?',
            answer:
              'Pure 100% cotton, wool, linen, hemp, and Tencel/lyocell shed essentially zero microplastics, they shed only natural cellulose fibres which biodegrade. Cotton blended with polyester (cotton-poly blends) still sheds the polyester portion.',
          },
          {
            question: 'Does washing my clothes less actually help?',
            answer:
              'Yes meaningfully. Each wash cycle is the primary microfibre-release event. Wearing items longer between washes, spot-cleaning rather than full-washing, and consolidating loads cuts emissions proportionally. Most clothing does not need washing every wear.',
          },
          {
            question: 'Do Guppyfriend bags actually work?',
            answer:
              'Yes. A Guppyfriend laundry bag captures roughly 70-90% of microfibres released during washing, based on independent testing. PlanetCare and other washing-machine filters are even more effective (90%+). Both are practical mitigation while reducing synthetic-fibre consumption itself.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Yakovenko N, et al.',
            year: 2025,
            title: 'Human exposure to PM10 microplastics in indoor air',
            publisher: 'PLOS One',
            url: 'https://journals.plos.org/plosone/article?id=10.1371%2Fjournal.pone.0328011',
          },
          {
            authors: 'De Falco F, Cocca M, Avella M, Lettieri E',
            year: 2020,
            title: 'Microfiber release to water, via laundering, and to air, via everyday use: a comparison between polyester clothing with differing textile parameters',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.9b06892',
          },
          {
            authors: 'Browne MA, Crump P, Niven SJ, et al.',
            year: 2011,
            title: 'Accumulation of microplastic on shorelines worldwide: sources and sinks',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/es201811s',
          },
          {
            authors: 'Wright SL, Ulke J, Font A, et al.',
            year: 2020,
            title: 'Atmospheric microplastic deposition in an urban environment and an evaluation of transport',
            publisher: 'Environment International',
            url: 'https://www.sciencedirect.com/science/article/pii/S0160412019320185',
          },
          {
            authors: 'WHO',
            year: 2022,
            title: 'Dietary and inhalation exposure to nano- and microplastic particles',
            publisher: 'World Health Organization',
            url: 'https://www.who.int/publications/i/item/9789240054608',
          },
          {
            authors: 'OEKO-TEX',
            year: 2024,
            title: 'Standard 100: testing for harmful substances in textiles',
            publisher: 'OEKO-TEX',
            url: 'https://www.oeko-tex.com/en/our-standards/oeko-tex-standard-100',
          },
        ]}
      />


    </BlogPostLayout>
  )
}
